/**
 * Procedural game audio — no sample files, everything synthesised in WebAudio.
 *
 * The engine is three detuned sawtooth oscillators (fundamental plus two
 * harmonics) run through a low-pass whose cutoff opens with revs, so it growls
 * at idle and screams near the limiter. Tyre scrub, wind rush and the off-road
 * rumble are three taps off one looping white-noise buffer, each with its own
 * filter. One-shots (countdown beeps, impacts, boost) are built from short
 * envelopes on throwaway nodes.
 *
 * Browsers refuse to start audio without a gesture, so the context is created
 * suspended and resumed on the first click or keypress. The mute state is
 * remembered in localStorage.
 */

const STORAGE_KEY = 'wkr-muted';

/** The per-frame driving state the mix reacts to. */
export interface DriveAudio {
  /** Engine revs, 0 (idle) .. 1 (redline). */
  rpm: number;
  /** How hard the engine is pulling, 0 .. 1 — drives harmonic bite. */
  load: number;
  /** Sideways scrub in units/second; above ~4 the tyres start to howl. */
  slip: number;
  /** 0 on tarmac .. 1 deep off-road. */
  offRoad: number;
  boosting: boolean;
  /** Ground speed as a fraction of top speed, for wind noise. */
  speed01: number;
}

/** Resolve the AudioContext constructor across older WebKit builds. */
function audioContextCtor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') return undefined;
  const legacy = window as unknown as { webkitAudioContext?: typeof AudioContext };
  return window.AudioContext ?? legacy.webkitAudioContext;
}

export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;

  // Engine voice.
  private oscillators: OscillatorNode[] = [];
  private oscGains: GainNode[] = [];
  private engineFilter: BiquadFilterNode | null = null;
  private engineGain: GainNode | null = null;

  // Noise taps.
  private screechGain: GainNode | null = null;
  private screechFilter: BiquadFilterNode | null = null;
  private windGain: GainNode | null = null;
  private rumbleGain: GainNode | null = null;

  private noiseBuffer: AudioBuffer | null = null;
  private started = false;
  private mutedState: boolean;

  constructor() {
    this.mutedState = GameAudio.loadMuted();
    this.installGestureHook();
  }

  private static loadMuted(): boolean {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false; // private mode / storage disabled
    }
  }

  /** Build the graph on the first user gesture, as autoplay policy requires. */
  private installGestureHook(): void {
    const start = (): void => {
      window.removeEventListener('pointerdown', start);
      window.removeEventListener('keydown', start);
      void this.ensureStarted();
    };
    window.addEventListener('pointerdown', start, { once: false });
    window.addEventListener('keydown', start, { once: false });
  }

  private async ensureStarted(): Promise<void> {
    if (this.started) {
      if (this.ctx?.state === 'suspended') await this.ctx.resume();
      return;
    }
    const Ctor = audioContextCtor();
    if (!Ctor) return;

    this.started = true;
    const ctx = new Ctor();
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.value = this.mutedState ? 0 : 0.55;
    this.master.connect(ctx.destination);

    this.buildEngine(ctx, this.master);
    this.buildNoise(ctx, this.master);

    if (ctx.state === 'suspended') await ctx.resume();
  }

  /** Three sawtooths through a resonant low-pass: the whole engine voice. */
  private buildEngine(ctx: AudioContext, out: GainNode): void {
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 500;
    filter.Q.value = 3.5;

    const gain = ctx.createGain();
    gain.gain.value = 0;
    filter.connect(gain);
    gain.connect(out);

    // Fundamental, octave and fifth-ish, each quieter than the last.
    const harmonics = [
      { multiple: 1, level: 0.5, detune: 0 },
      { multiple: 2, level: 0.28, detune: 7 },
      { multiple: 3.02, level: 0.16, detune: -9 },
    ];
    for (const h of harmonics) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 60 * h.multiple;
      osc.detune.value = h.detune;
      const g = ctx.createGain();
      g.gain.value = h.level;
      osc.connect(g);
      g.connect(filter);
      osc.start();
      this.oscillators.push(osc);
      this.oscGains.push(g);
    }

    this.engineFilter = filter;
    this.engineGain = gain;
  }

  /** One looping noise buffer feeding the tyre, wind and rumble taps. */
  private buildNoise(ctx: AudioContext, out: GainNode): void {
    const length = Math.floor(ctx.sampleRate * 2);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buffer;

    const tap = (
      type: BiquadFilterType,
      frequency: number,
      q: number,
    ): { gain: GainNode; filter: BiquadFilterNode } => {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = type;
      filter.frequency.value = frequency;
      filter.Q.value = q;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(filter);
      filter.connect(gain);
      gain.connect(out);
      source.start();
      return { gain, filter };
    };

    const screech = tap('bandpass', 2100, 7);
    this.screechGain = screech.gain;
    this.screechFilter = screech.filter;
    this.windGain = tap('highpass', 700, 0.7).gain;
    this.rumbleGain = tap('lowpass', 240, 1.2).gain;
  }

  // -------------------------------------------------------------------------
  // Mixing
  // -------------------------------------------------------------------------

  /** Smoothly chase a target so parameter changes never click. */
  private ramp(param: AudioParam | undefined | null, target: number, time = 0.06): void {
    if (!param || !this.ctx) return;
    param.setTargetAtTime(target, this.ctx.currentTime, time);
  }

  /** Feed one frame of driving state into the mix. */
  updateDrive(d: DriveAudio): void {
    if (!this.ctx || this.mutedState) return;

    // Revs drive both pitch and how far the low-pass opens.
    const base = 46 + d.rpm * 108 + (d.boosting ? 22 : 0);
    for (let i = 0; i < this.oscillators.length; i++) {
      const multiple = [1, 2, 3.02][i] ?? 1;
      this.ramp(this.oscillators[i].frequency, base * multiple, 0.05);
    }
    this.ramp(this.engineFilter?.frequency, 380 + d.rpm * 2600 + d.load * 700, 0.06);
    this.ramp(this.engineGain?.gain, 0.10 + d.rpm * 0.11 + (d.boosting ? 0.06 : 0), 0.08);

    // Tyres howl once the kart is genuinely scrubbing sideways.
    const scrub = Math.min(1, Math.max(0, (d.slip - 3.5) / 12));
    this.ramp(this.screechGain?.gain, scrub * 0.14 * (1 - d.offRoad), 0.05);
    this.ramp(this.screechFilter?.frequency, 1500 + scrub * 1800, 0.05);

    this.ramp(this.windGain?.gain, d.speed01 ** 2 * 0.075, 0.12);
    this.ramp(this.rumbleGain?.gain, d.offRoad * d.speed01 * 0.16, 0.08);
  }

  /** Drop the driving voices to silence (menus, results, disconnect). */
  silenceDrive(): void {
    this.ramp(this.engineGain?.gain, 0, 0.15);
    this.ramp(this.screechGain?.gain, 0, 0.1);
    this.ramp(this.windGain?.gain, 0, 0.15);
    this.ramp(this.rumbleGain?.gain, 0, 0.15);
  }

  // -------------------------------------------------------------------------
  // One-shots
  // -------------------------------------------------------------------------

  /** Short envelope on a throwaway oscillator. */
  private blip(frequency: number, duration: number, level: number, type: OscillatorType): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || this.mutedState) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, now);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(level, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain);
    gain.connect(master);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  /** Countdown pip; `go` plays the higher, longer "lights out" tone. */
  countdown(go: boolean): void {
    this.blip(go ? 880 : 440, go ? 0.55 : 0.18, go ? 0.3 : 0.2, 'square');
  }

  /**
   * Gate chime. A missed checkpoint gets a low, flat buzz rather than a quieter
   * version of the good one — at racing speed the banner is gone before it is
   * read, so the two events have to be told apart by ear alone.
   */
  checkpoint(missed: boolean): void {
    if (missed) {
      this.blip(150, 0.3, 0.22, 'sawtooth');
      this.blip(112, 0.34, 0.18, 'square');
      return;
    }
    this.blip(1046, 0.12, 0.13, 'triangle');
  }

  /** Kart-to-kart or barrier hit. `strength` 0..1 scales thump and clatter. */
  impact(strength: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.noiseBuffer || this.mutedState) return;
    const level = Math.min(1, Math.max(0.1, strength));
    const now = ctx.currentTime;

    // Body thump.
    const thump = ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(150, now);
    thump.frequency.exponentialRampToValueAtTime(48, now + 0.16);
    const thumpGain = ctx.createGain();
    thumpGain.gain.setValueAtTime(0.34 * level, now);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    thump.connect(thumpGain);
    thumpGain.connect(master);
    thump.start(now);
    thump.stop(now + 0.25);

    // Panel clatter.
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1600;
    band.Q.value = 1.2;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.22 * level, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    noise.connect(band);
    band.connect(noiseGain);
    noiseGain.connect(master);
    noise.start(now);
    noise.stop(now + 0.2);
  }

  /** Rising whoosh as the boost lights up. */
  boost(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.noiseBuffer || this.mutedState) return;
    const now = ctx.currentTime;

    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    const sweep = ctx.createBiquadFilter();
    sweep.type = 'bandpass';
    sweep.frequency.setValueAtTime(400, now);
    sweep.frequency.exponentialRampToValueAtTime(3600, now + 0.34);
    sweep.Q.value = 1.6;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
    noise.connect(sweep);
    sweep.connect(gain);
    gain.connect(master);
    noise.start(now);
    noise.stop(now + 0.45);
  }

  // -------------------------------------------------------------------------
  // Mute
  // -------------------------------------------------------------------------

  get muted(): boolean {
    return this.mutedState;
  }

  setMuted(muted: boolean): void {
    this.mutedState = muted;
    try {
      window.localStorage.setItem(STORAGE_KEY, muted ? '1' : '0');
    } catch {
      /* storage unavailable; the setting just won't persist */
    }
    if (muted) this.silenceDrive();
    this.ramp(this.master?.gain, muted ? 0 : 0.55, 0.08);
    if (!muted) void this.ensureStarted();
  }

  dispose(): void {
    for (const osc of this.oscillators) {
      try {
        osc.stop();
      } catch {
        /* already stopped */
      }
    }
    this.oscillators = [];
    void this.ctx?.close();
    this.ctx = null;
  }
}
